'use client';

import Card from '@/components/Card/Card';
import { InlineAlert, InlineAlertTitle } from '@/components/alert';
import { Button } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import SubmitWrapper from '@/components/helperTextWrapper/SubmitWrapper';
import InlineTextLink from '@/components/inlineTextLink/InlineTextLink';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import { RichText } from '@/components/richText/RichText';
import TextCard from '@/components/textCard/TextCard';
import { getContractSpecificReplacementTexts, getReplacementTexts } from '@/helper/contractConclusionUtils';
import { ProductCategory } from '@/helper/enums';
import { type ReplacementsType } from '@/helper/replacements';
import { getFullSlugFromMultilink, linkToString } from '@/helper/urls';
import { useHeaderStore } from '@/providers/HeaderStoreProvider';
import { useOrderFormSettings } from '@/providers/OrderFormSettingsProvider';
import classes from '@/storyblok/PageContractConclusion/PageContractConclusion.module.scss';
import classNames from 'classnames';
import { Formik, type FormikProps } from 'formik';
import { t } from 'i18next';
import { useRouter } from 'next/navigation';
import { type FC, Fragment, useRef, useState, useTransition } from 'react';
import * as Yup from 'yup';

import PageContractConclusionForm from './PageContractConclusionForm';
import { getDescription, getLabel } from './PageContractConclusionHandler';
import {
  type ContractData,
  CustomerType,
  PageContractConclusionOrderType,
  type PageContractConclusionProps,
} from './PageContractConclusionTypes';
import { confirmOrderData } from './handleData';
import { normalizeOrderData } from './normalizeOrderData';

const PageContractConclusion: FC<PageContractConclusionProps> = ({
  blok,
  urlParams,
  orderData,
  isMockMode = false,
  mockKey,
  mockFixture,
}) => {
  const secondSubmitButtonRef = useRef<HTMLButtonElement | null>(null);
  const pageContractConclusionFormRef = useRef<HTMLDivElement | null>(null);

  const [isPending, startTransition] = useTransition();

  const orderSettings = useOrderFormSettings();
  const [isSese, setIsSese] = useState(false);
  const router = useRouter();

  const setErrorModalOpen = useHeaderStore((state) => state.setErrorModalOpen);
  const handleScrollToSubmit = () => {
    if (secondSubmitButtonRef.current) {
      secondSubmitButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const normalizedOrderData = normalizeOrderData(orderData);

  const contracts: ContractData[] = normalizedOrderData.contractData || [];

  const showEnfgCheck = contracts.some((contract) => /S10.*NAT/.test(contract.tariffProductId));

  const fullName = `${normalizedOrderData.personalData?.salutation || ''} ${normalizedOrderData.personalData?.firstname || ''} ${normalizedOrderData.personalData?.lastname || ''}`;

  const isOrganization = normalizedOrderData.customerType === CustomerType.Organization;
  const isGroup = normalizedOrderData.customerType === CustomerType.Group;
  const hasContractPartner = isGroup && !!normalizedOrderData.contractPartnerData.firstname;

  const showSelfServiceRegistration =
    normalizedOrderData.selfServiceRegistration === 'display_checkbox' ||
    normalizedOrderData.selfServiceRegistration === 'display_agb';
  const showOnlyAGB = normalizedOrderData.selfServiceRegistration === 'display_agb';
  const showSelfServiceCheckbox = normalizedOrderData.selfServiceRegistration === 'display_checkbox';
  const seseNoDisplay = normalizedOrderData.selfServiceRegistration === 'no_display';

  const hasRegulatedProducts =
    normalizedOrderData.regulatedProducts && normalizedOrderData.regulatedProducts.length > 0;

  const hasDifferentInvoice = normalizedOrderData.invoiceAddress?.differentInvoiceAddress;

  const hasIncentiveCode = normalizedOrderData.incentiveData?.code && normalizedOrderData.incentiveData.code !== '';
  const hasIncentive =
    hasIncentiveCode && normalizedOrderData.orderType !== PageContractConclusionOrderType.ProductChange;

  const isCustomerRecommendationViaCode =
    normalizedOrderData.customerRecommendationViaCodeData?.kwkCode &&
    normalizedOrderData.customerRecommendationViaCodeData.kwkCode !== '';

  const showMarketingOptIn = normalizedOrderData.optInVisible;
  const commonReplacements: ReplacementsType = getReplacementTexts(
    normalizedOrderData.personalData ?? {},
    normalizedOrderData.contractPartnerData ?? {},
    normalizedOrderData?.customerType?.toString() ?? ''
  );

  const handleSubmit = async (
    formikProps: FormikProps<{
      acceptTerms: boolean;
      acceptPrivacy: boolean;
      receiveMarketing: boolean;
      enfgCheck: boolean;
    }>
  ) => {
    await formikProps.setTouched(
      {
        acceptTerms: true,
        acceptPrivacy: true,
        receiveMarketing: true,
        ...(showEnfgCheck ? { enfgCheck: true } : {}),
      },
      false
    );
    const validationErrors = await formikProps.validateForm();

    if (Object.keys(validationErrors).length > 0) {
      pageContractConclusionFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    /* 
      Logic for Sese
      in case selfServiceRegistration is set to no_display, the value is false
      in case selfServiceRegistration is set to display_abg, the value is true
      in case selfServiceRegistration is set to display_checkbox, the value depends on the form value
    */
    const selfServiceRegistration = !seseNoDisplay && (showOnlyAGB || (showSelfServiceCheckbox && isSese));

    /*
      Logic for Marketing OptIn
      in case the value of optInVisible is set to "X" and the checkbox of the value receiveMarketing is checked, the value is true
      in case the value of optInVisible is empty, the value is false
    */
    const optInAds = showMarketingOptIn && formikProps.values.receiveMarketing;

    startTransition(async () => {
      try {
        await confirmOrderData(urlParams.orderGuid, urlParams.tokenGuid, optInAds, selfServiceRegistration, {
          mockKey,
          mockFixture,
        });

        if (isOrganization) {
          const gkSlug = getFullSlugFromMultilink(blok?.thankyou_page_gk);
          if (gkSlug) router.push(linkToString(gkSlug));
        } else {
          const pkSlug = getFullSlugFromMultilink(blok?.thankyou_page_pk);
          if (pkSlug) router.push(linkToString(pkSlug));
        }
      } catch (err) {
        setErrorModalOpen(true);
        console.error('Order submission failed:', err);
      }
    });
  };

  const validationSchema = Yup.object().shape({
    acceptTerms: Yup.boolean()
      .required('Die genannten Bedingungen müssen akzeptiert werden, um fortfahren zu können')
      .oneOf([true], 'Die genannten Bedingungen müssen akzeptiert werden, um fortfahren zu können'),
    acceptPrivacy: Yup.boolean()
      .required('Die Widerrufsbedingungen müssen akzeptiert werden, um fortfahren zu können')
      .oneOf([true], 'Die Widerrufsbedingungen müssen akzeptiert werden, um fortfahren zu können'),
    receiveMarketing: Yup.boolean(),
    enfgCheck: showEnfgCheck
      ? Yup.boolean()
          .required('Diese Bestätigung ist erforderlich, um fortfahren zu können')
          .oneOf([true], 'Diese Bestätigung ist erforderlich, um fortfahren zu können')
      : Yup.boolean(),
  });

  return isPending ? (
    <LoadingBackdrop loading text={t('Daten werden gespeichert...')} />
  ) : (
    <Formik
      initialValues={{
        acceptTerms: false,
        acceptPrivacy: false,
        receiveMarketing: false,
        enfgCheck: false,
      }}
      validateOnChange={true}
      validateOnBlur={true}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        console.log('Form submitted with values:', values);
      }}
    >
      {(formikProps) => (
        <div data-testid="pagecontractconclusion" className={classes.contractConclusion}>
          <div className="grid-wrapper content-bloks-wrapper">
            <div className={classes.contentWrapper}>
              {isMockMode && (
                <InlineAlert severity="warning" variant="standard">
                  <InlineAlertTitle>{t('Testmodus')}</InlineAlertTitle>
                  <p>
                    {t('Es wird keine echte Bestellung ausgelöst. Fixture')}: {mockFixture}
                  </p>
                </InlineAlert>
              )}
              <div className={classes.headline}>
                <HeadlineField className={classes.headline__text} tag={HeadlineTag.H1} style={HeadlineTag.H4}>
                  {blok.headline}
                </HeadlineField>
                <Card variant="Box" className={classes.orderCard}>
                  {t('Bestellnummer')}: {` ${urlParams.orderGuid}`}
                </Card>
              </div>
              <RichText className={classes.greeting} text={blok.greeting} replace={commonReplacements} />
              <RichText className={classes.intro} text={blok.intro} />
              <div className={classes.buttonWrapper}>
                <SubmitWrapper formikErrors={formikProps.errors} formikTouched={formikProps.touched}>
                  <Button
                    data-testid="CC-page-order-scroll-button"
                    onClick={handleScrollToSubmit}
                    className={classes.orderButton}
                  >
                    {t('Jetzt kostenpflichtig bestellen')}
                  </Button>
                </SubmitWrapper>
              </div>
              <Card variant="Box" className={classes.contractInfoCard}>
                <div className={classes.contractInfo__row}>
                  <HeadlineField className={classes.contractInfo__headline} tag={HeadlineTag.H2} style={HeadlineTag.H6}>
                    {t('Auftragsart')}
                  </HeadlineField>
                  <p className={classes.contractInfo__subHeadline}>{getLabel(normalizedOrderData.orderType)}</p>
                  <p className={classes.contractInfo__body}>{getDescription(normalizedOrderData.orderType)}</p>
                </div>
                <div className={classes.contractInfo__row}>
                  <HeadlineField className={classes.contractInfo__headline} tag={HeadlineTag.H2} style={HeadlineTag.H6}>
                    {t('Persönliche Daten')}
                  </HeadlineField>
                  <p className={classes.contractInfo__subHeadline}>{!isOrganization ? t('Name') : t('Firma')}</p>
                  <p className={classes.contractInfo__body}>
                    {!isOrganization ? fullName : normalizedOrderData.personalData.company}
                  </p>
                  {hasContractPartner && (
                    <div>
                      <p className={classes.contractInfo__subHeadline}>{t('Weitere Vertragspartner')}</p>
                      <p
                        className={classes.contractInfo__body}
                      >{`${normalizedOrderData.contractPartnerData?.salutation || ''} ${normalizedOrderData.contractPartnerData.firstname || ''} ${normalizedOrderData.contractPartnerData.lastname || ''}`}</p>
                    </div>
                  )}
                </div>
                {isOrganization && (
                  <div className={classes.contractInfo__row}>
                    <HeadlineField
                      className={classes.contractInfo__headline}
                      tag={HeadlineTag.H2}
                      style={HeadlineTag.H6}
                    >
                      {t('Vertretungsberechtigte Person')}
                    </HeadlineField>

                    <p
                      className={classes.contractInfo__body}
                    >{`${normalizedOrderData.personalData?.salutation || ''} ${normalizedOrderData.personalData.firstname || ''} ${normalizedOrderData.personalData.lastname || ''}`}</p>
                  </div>
                )}
                {isCustomerRecommendationViaCode && (
                  <div className={classes.contractInfo__row}>
                    <HeadlineField
                      className={classes.contractInfo__headline}
                      tag={HeadlineTag.H2}
                      style={HeadlineTag.H6}
                    >
                      {t('Kunden werben Kunden')}
                    </HeadlineField>
                    <p className={classes.contractInfo__body}>
                      {t('Sie wurden erfolgreich von einem Kunden der Stadtwerke Düsseldorf geworben.')}
                    </p>
                  </div>
                )}

                {hasDifferentInvoice && (
                  <div className={classes.contractInfo__row}>
                    <HeadlineField
                      className={classes.contractInfo__headline}
                      tag={HeadlineTag.H2}
                      style={HeadlineTag.H6}
                    >
                      {t('Abweichende Rechnungsadresse')}
                    </HeadlineField>
                    <p className={classes.contractInfo__body}>
                      {normalizedOrderData.invoiceAddress.street + ' ' + normalizedOrderData.invoiceAddress.hnr}
                      <br />
                      {normalizedOrderData.invoiceAddress.zip + ' ' + normalizedOrderData.invoiceAddress.city}
                    </p>
                  </div>
                )}
                {!!normalizedOrderData.invoiceAddress.postBox && (
                  <div className={classes.contractInfo__row}>
                    <HeadlineField
                      className={classes.contractInfo__headline}
                      tag={HeadlineTag.H2}
                      style={HeadlineTag.H6}
                    >
                      {t('Postfach')} {normalizedOrderData.invoiceAddress.postBox}
                    </HeadlineField>
                  </div>
                )}
                <>
                  <HeadlineField className={classes.contractInfo__headline} tag={HeadlineTag.H2} style={HeadlineTag.H6}>
                    {t('Zahlungsweise')}
                  </HeadlineField>
                  {normalizedOrderData.paymentData.sepaCheck ? (
                    <div className={classes.contractInfo__body}>
                      <p>{blok.sepa_text}</p>
                      <p>
                        <strong>
                          {t('IBAN')}: {normalizedOrderData.paymentData.iban}
                        </strong>
                      </p>
                      <p>{t('Sie können Ihre Zahlungsweise jederzeit umstellen.')}</p>
                    </div>
                  ) : (
                    <p className={classes.contractInfo__body}>{blok.transfer_text}</p>
                  )}

                  {hasIncentive && isCustomerRecommendationViaCode && (
                    <>
                      <p className={classes.contractInfo__subHeadline}>
                        {t('Aktionscode')} {normalizedOrderData.incentiveData.code}
                      </p>
                      <p className={classes.contractInfo__body}>{normalizedOrderData.incentiveData.descriptionText}</p>
                    </>
                  )}
                </>
              </Card>
              {contracts.map((contract, index) => {
                return (
                  <div
                    data-testid={`CC-page-contract-${contract.tariffProductName}-${index}`}
                    key={`contract-${contract.tariffProductName}-${index}`}
                    className={classes.boxWrapper}
                  >
                    <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H6}>
                      {t('Bestellung')} {contracts.length > 1 ? index + 1 : ''}
                    </HeadlineField>
                    <div className={classes.cardContainer}>
                      <Card className={classes.contract} variant="Box" key={`contractInfo-${index}`}>
                        <div className={classes.contractCard}>
                          <div className={classes.contractCard__data}>
                            <span>
                              <p className={classes.contractCard__subHeadline}>{t('Gewünschter Lieferbeginn')}</p>
                              <p className={classes.contractCard__body}>{contract.deliveryDesiredBeginning}</p>
                            </span>
                            <span>
                              <p className={classes.contractCard__subHeadline}>
                                {contract.tariffProductCategory.toLowerCase() === ProductCategory.Electricity
                                  ? 'Stromzählernummer'
                                  : 'Gaszählernummer'}
                              </p>
                              <p className={classes.contractCard__body}>{contract.counterNumber}</p>
                            </span>
                            {contract.counterReading && (
                              <span>
                                <p className={classes.contractCard__subHeadline}>
                                  {t('Zählerstand zum Einzug in kWh')}
                                </p>

                                <p className={classes.contractCard__body}> {contract.counterReading}</p>
                              </span>
                            )}
                            <span>
                              <p className={classes.contractCard__subHeadline}>{t('Lieferadresse')}</p>
                              <p className={classes.contractCard__body}>
                                {contract.shippingAddress.street + ' ' + contract.shippingAddress.hnr} <br />
                                {contract.shippingAddress.zip + ' ' + contract.shippingAddress.city}
                              </p>
                            </span>
                            {contract.utilitiesChangeAccountProvider && (
                              <span>
                                <p className={classes.contractCard__subHeadline}>{t('Bisherige Versorger')}</p>
                                <p className={classes.contractCard__body}> {contract.utilitiesChangeAccountProvider}</p>
                              </span>
                            )}
                            {contract.utilitiesChangeCancellationDoneWhen && (
                              <span>
                                <p className={classes.contractCard__subHeadline}>{t('Kündigungsdatum')}</p>
                                <p className={classes.contractCard__body}>
                                  {contract.utilitiesChangeCancellationDoneWhen}
                                </p>
                              </span>
                            )}
                          </div>
                          <p className={classes.contractCard__body}>
                            <strong>
                              Für weitere Informationen zu diesem Produkt, lesen Sie bitte die&nbsp;
                              <InlineTextLink href="#legalWrapper">Hinweise</InlineTextLink>
                            </strong>
                          </p>
                          <p
                            className={classNames(classes.contractCard__agb, classes.contractCard__body)}
                            dangerouslySetInnerHTML={{
                              __html: contract.textAgb,
                            }}
                          ></p>
                          <p className={classes.contractCard__body}>
                            {t(
                              'Dieser Stromlieferungsvertrag kommt mit schriftlicher Bestätigung der Stadtwerke Düsseldorf AG zustande.'
                            )}
                          </p>
                        </div>
                      </Card>

                      <Card
                        header={
                          <HeadlineField tag={HeadlineTag.H2} style={HeadlineTag.H6}>
                            {contract.tariffProductName}
                          </HeadlineField>
                        }
                        variant="Summary"
                        className={classes.productInfo}
                      >
                        <dl className={classes.values}>
                          <dt className={classes.label}>{t('Arbeitspreis')}</dt>
                          <dd>
                            <span className={classes.value}>{contract.tariffProductWorkingPrice}</span>{' '}
                            <span className={classes.unit}>{t('ct / kWh')}</span>
                          </dd>
                          <dt className={classes.label}>{t('Grundpreis')}</dt>
                          <dd>
                            <span className={classes.value}>{contract.tariffProductBasePrice}</span>{' '}
                            <span className={classes.unit}>{t('€ / Jahr')}</span>
                          </dd>
                          <dt className={classes.label}>{t('Verbrauch')}</dt>
                          <dd>
                            <span className={classes.value}>{contract.tariffConsumption}</span>{' '}
                            <span className={classes.unit}>{t('kWh / Jahr')}</span>
                          </dd>
                          {contract.tariffProductBonus && contract.tariffProductBonus !== '0,00' && (
                            <>
                              <dt className={classes.label}>{t('Bonus')}</dt>
                              <dd>
                                <span className={classes.value}>{`${contract.tariffProductBonus} €`}</span>{' '}
                                <span className={classes.unit}>{t('einmalig')}</span>
                              </dd>
                            </>
                          )}
                          {contract.tariffProductBonusDirect && contract.tariffProductBonusDirect !== '0,00' && (
                            <>
                              <dt className={classes.label}>{t('Sofortbonus')}</dt>
                              <dd>
                                <span className={classes.value}>{`${contract.tariffProductBonusDirect} €`}</span>{' '}
                                <span className={classes.unit}>{t('einmalig')}</span>
                              </dd>
                            </>
                          )}
                        </dl>
                      </Card>
                    </div>
                  </div>
                );
              })}
              {hasRegulatedProducts && (
                <InlineAlert severity="info" variant="standard">
                  <InlineAlertTitle>{t('Info')}</InlineAlertTitle>
                  <p>{normalizedOrderData.regulatedProducts}</p>
                </InlineAlert>
              )}
              <div ref={pageContractConclusionFormRef}>
                <PageContractConclusionForm showMarketingOptIn={showMarketingOptIn} showEnfgCheck={showEnfgCheck} />
              </div>
              {showSelfServiceRegistration && (
                <TextCard
                  image={orderSettings?.customer_portal_image}
                  heading={orderSettings?.customer_portal_header}
                  text={orderSettings?.customer_portal_text}
                  checkboxText={orderSettings?.customer_portal_checkbox_text}
                  showCheckbox={showSelfServiceCheckbox}
                  checkboxChangeHandler={() => setIsSese(!isSese)}
                  showOnlyAGB={showOnlyAGB}
                />
              )}
              <div className={classes.buttonWrapper}>
                <SubmitWrapper formikErrors={formikProps.errors} formikTouched={formikProps.touched}>
                  <Button
                    data-testid="CC-page-order-button"
                    ref={secondSubmitButtonRef}
                    onClick={() => handleSubmit(formikProps)}
                    className={classes.orderButton}
                  >
                    {t('Jetzt kostenpflichtig bestellen')}
                  </Button>
                </SubmitWrapper>
              </div>
              <div className={classes.floatingSubmitButton}>
                <SubmitWrapper formikErrors={formikProps.errors} formikTouched={formikProps.touched}>
                  <Button
                    data-testid="CC-page-order-button"
                    onClick={() => handleSubmit(formikProps)}
                    className={classes.orderButton}
                  >
                    {t('Jetzt kostenpflichtig bestellen')}
                  </Button>
                </SubmitWrapper>
              </div>
            </div>
            <div id="legalWrapper" className={classes.legal__wrapper}>
              <strong>{t('Hinweise')}:</strong>
              {contracts.map((contract, index) => {
                const contractReplacements = [...commonReplacements, ...getContractSpecificReplacementTexts(contract)];

                return (
                  <Fragment key={`${contract.tariffProductName}-key-${index}`}>
                    <p>
                      <strong>{contract.tariffProductName}</strong>
                    </p>
                    <RichText
                      returnFragment
                      text={
                        normalizedOrderData.customerType === CustomerType.Individual
                          ? blok.legal_notes
                          : normalizedOrderData.customerType === CustomerType.Organization
                            ? blok.commercial_legal_notes
                            : undefined
                      }
                      replace={contractReplacements}
                    />
                    {normalizedOrderData.customerType === CustomerType.Organization &&
                      blok.commercial_customer_appendix_electricity && (
                        <RichText returnFragment text={blok.commercial_customer_appendix_electricity} />
                      )}
                    {contract.textMsb && contract.textMsb.length > 0 && (
                      <>
                        <p>
                          <strong>{t('Hinweise zum Messstellenbetrieb')}</strong>
                        </p>
                        {contract.textMsb && contract.textMsb.length > 0 && (
                          <p
                            dangerouslySetInnerHTML={{
                              __html: contract.textMsb,
                            }}
                          ></p>
                        )}
                      </>
                    )}
                    {contract.textMinDuration && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: contract.textMinDuration,
                        }}
                      ></p>
                    )}
                    {contract.terminationNotice && contract.terminationNotice?.length > 0 && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: contract.terminationNotice,
                        }}
                      ></p>
                    )}
                    {contract.textPriceGuarantee && (
                      <>
                        <p>
                          <strong>{t('Hinweis zur Preisgarantie')}</strong>
                        </p>
                        <p
                          dangerouslySetInnerHTML={{
                            __html: contract.textPriceGuarantee,
                          }}
                        ></p>
                      </>
                    )}
                    {contract.tariffProductBonus && contract.tariffProductBonus !== '0,00' && (
                      <RichText returnFragment replace={contractReplacements} text={blok.bonus} />
                    )}
                    {contract.tariffProductBonusDirect &&
                      contract.tariffProductBonusDirect !== '0,00' &&
                      blok.bonus_direct && (
                        <RichText returnFragment replace={contractReplacements} text={blok.bonus_direct} />
                      )}
                  </Fragment>
                );
              })}
              <RichText returnFragment text={orderSettings?.order_form_additonal_text} />
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default PageContractConclusion;
